import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_MANAGE_COMPANY_SOP,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  PAGE_ROUTE_SOPS,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { MANAGE_STORES_SOP_SECTIONS } from "./constants/manageStoresSopContent";
import { MANAGE_STORES_SOP_IMAGES } from "./constants/manageStoresSopImages";
import type { ManageStoresSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const ManageStoresSopImage: React.FC<{
  image: ManageStoresSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const ManageStoresSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const runBooksPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const manageCompanyPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_MANAGE_COMPANY_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_MANAGE_COMPANY_SOP}`;
  const rolesPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ROLES_AND_ACCESS, language, defaultLang)
      : `/${PAGE_ROUTE_ROLES_AND_ACCESS}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="manage-stores-sop-page"
        eyebrow={t("manage-stores-sop-page-eyebrow", "Run books")}
        title={t("manage-stores-sop-page-title", "How to manage stores")}
        intro={t(
          "manage-stores-sop-page-intro",
          "Set up and run each shop location. Add stores, keep details up to date, and manage the team, customers, and suppliers linked to every branch."
        )}
      />

      <PublicInfoCallout testId="manage-stores-sop-page-note">
        {t(
          "manage-stores-sop-page-note",
          "For company admins and store managers. Company admins can work across all stores. Store managers usually see only their assigned location. If a menu or button is missing, ask your company admin to check your role."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {MANAGE_STORES_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`manage-stores-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            {section.introKey != null ? (
              <Typography
                variant="body1"
                sx={{ ...publicInfoBodySx, mb: 2 }}
                data-testid={`manage-stores-sop-intro-${section.id}`}
              >
                {t(section.introKey, section.introFallback ?? "")}
              </Typography>
            ) : null}
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? MANAGE_STORES_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <ManageStoresSopImage
                        image={image}
                        testId={`manage-stores-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t(
          "manage-stores-sop-page-cta-title",
          "Managing the whole company?"
        )}
        testId="manage-stores-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "manage-stores-sop-page-cta-body",
            "Company-wide employee and subscription tasks live on the Company page. See roles and access if you are unsure who can do what."
          )}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-stores-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("manage-stores-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={manageCompanyPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "manage-stores-sop-page-cta-manage-company-link",
              "How to manage your company"
            )}
          </MuiLink>
          {t("manage-stores-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={rolesPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-stores-sop-page-cta-roles-link", "Roles & access")}
          </MuiLink>
          {t("manage-stores-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-stores-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("manage-stores-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-stores-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default ManageStoresSopPage;
